import { Tabs, Tab } from '@ds';
import { useContext, useMemo } from 'react';
import type { Key } from '@react-types/shared';
import { AuthContext } from '@lp/core/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdMyLocation } from 'react-icons/md';
import { caseSectionTabs } from '@features/store/case-section-tabs.store';
import CaseDetailsTab from './CaseDetails/CaseDetailsTab';
import { AssignedResourcesTab } from './AssignedResources/AssignedResourcesTab';
import { ClinicalDataTab } from './ClinicalData/ClinicalDataTab';
import { Case, CaseStatus } from '@/api/apis/case';
import { ArchiveCasesPermissions, CaseDetailsPermissions } from '@/permissions';
import { CaseLogTab } from './CaseLogTab/CaseLogTab';
import CaseAccessTab from './CaseAccess/CaseAccessTab';
import { getCaseCurrentSubRoute, getCaseSelectedTabKey, getCaseTabNavigatePath } from './case-tabs.utils';
import { LocationTrackingTab } from './LocationTracking/LocationTrackingTab';
import { useCanViewLocationTracking } from './LocationTracking/useCanViewLocationTracking';
import { isAllowedStatus } from '@/utils/helpers';
import { useRouteByType } from '@/queries/route-planner';
import { RouteTypeEnum } from '@/api/apis/route';
import { checkTxCenterProvidersInTransit } from '@/utils/route';

const ACTIVE_LOCATION_TRACKING_TITLE = (
  <div className="flex gap-1 items-center uppercase">
    <MdMyLocation size={16} />
    Location Tracking
  </div>
);

type CaseTabsProps = {
  caseInfo: Case;
};

const CaseTabs = ({ caseInfo }: CaseTabsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkPermission } = useContext(AuthContext);
  const { data: routeData } = useRouteByType(RouteTypeEnum.Main, caseInfo.logisticsReference?.id);

  const hasClinicalDataViewPermission = checkPermission(CaseDetailsPermissions.ClinicalDataRead);
  const hasLogsViewPermission = checkPermission(CaseDetailsPermissions.CaseLogsRead);
  const canViewLocationTracking = useCanViewLocationTracking();

  const isArchiveCase = caseInfo.status === CaseStatus.Closed || caseInfo.status === CaseStatus.Finished;
  const hasArchiveAccessReadPermission =
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccess90DaysRead) ||
    checkPermission(ArchiveCasesPermissions.CaseArchiveAccessIndefiniteRead);

  const currentSubRoute = useMemo(
    () => getCaseCurrentSubRoute(location.pathname, caseInfo.caseId),
    [location.pathname, caseInfo.caseId],
  );

  const selectedKey = useMemo(() => getCaseSelectedTabKey(currentSubRoute), [currentSubRoute]);

  const onTabChange = (tab: Key) => {
    const tabKey = String(tab);

    if (tabKey !== caseSectionTabs.DETAILS && location.hash) {
      navigate(location.pathname + location.search, { replace: true });
    }

    navigate(getCaseTabNavigatePath(caseInfo.caseId, tabKey, location.search));
  };

  const locationTrackingTitle = useMemo(() => {
    const isActiveLocationTrackingStatus = isAllowedStatus({
      statusFrom: CaseStatus.OrganOnOCS,
      statusTo: CaseStatus.AtTXCenter,
      currentStatus: caseInfo.status,
    });

    const isOnlyTxCenterProviders = routeData?.legs ? checkTxCenterProvidersInTransit(routeData.legs) : false;
    const showActiveLocationTrackingTitle = !isOnlyTxCenterProviders && isActiveLocationTrackingStatus;

    return showActiveLocationTrackingTitle ? ACTIVE_LOCATION_TRACKING_TITLE : 'Location Tracking';
  }, [caseInfo.status, routeData?.legs]);

  return (
    <Tabs
      aria-label="Tabs"
      variant="underlined"
      selectedKey={selectedKey}
      onSelectionChange={onTabChange}
      classNames={{
        tabContent: 'text-m-cps',
        base: 'grid w-fit px-8 py-5',
        tabList: 'p-0 flex-wrap',
        tab: 'flex-1',
        panel: 'h-full overflow-auto px-8 pb-8 pt-0',
      }}
    >
      <Tab key={caseSectionTabs.DETAILS} title="Case Details">
        <CaseDetailsTab caseInfo={caseInfo} onTabChange={onTabChange} />
      </Tab>

      <Tab key={caseSectionTabs.RESOURCES} title="Assigned Resources">
        <AssignedResourcesTab caseInfo={caseInfo} />
      </Tab>

      {hasClinicalDataViewPermission && (
        <Tab key={caseSectionTabs.CLINICAL} title="Clinical Data">
          <ClinicalDataTab caseInfo={caseInfo} />
        </Tab>
      )}

      {hasLogsViewPermission && (
        <Tab key={caseSectionTabs.LOG} title="Case log">
          <CaseLogTab caseInfo={caseInfo} />
        </Tab>
      )}

      {hasArchiveAccessReadPermission && isArchiveCase && (
        <Tab key={caseSectionTabs.ACCESS} title="Access">
          <CaseAccessTab caseInfo={caseInfo} />
        </Tab>
      )}

      {canViewLocationTracking && (
        <Tab key={caseSectionTabs.LOCATION_TRACKING} title={locationTrackingTitle}>
          <LocationTrackingTab />
        </Tab>
      )}
    </Tabs>
  );
};

export default CaseTabs;
